import React from 'react';
import { V } from '../../utils/theme';
import { Icons } from '../../components/Icons';
import { Card } from '../../components/ui';

// ─── Admin hub ───
// Tiles route by dispatching TAB. Two of them (Organizations, Org Analytics) are
// super-admin only: the API gates those actions on ADMIN_EMAIL and answers
// `super_admin_required`, so the tile is disabled with the reason rather than
// letting a plain admin walk into a 403. `check` also reports whether ADMIN_EMAIL
// is configured at all — an unset ADMIN_EMAIL is a server misconfiguration, not a
// permissions decision, and says so.
export function AdminHub({ d, isSuperAdmin = null, adminEmailConfigured = true }) {
  const gateReason = adminEmailConfigured === false
    ? "ADMIN_EMAIL is not set on the API"
    : "Super-admin only";

  const Section = ({ icon, label, desc, color, tab, superOnly }) => {
    // Only a confirmed `false` locks the tile — `null` means the server has not
    // answered yet, and locking on an unknown would hide the screens from the one
    // person who owns them on every cold start.
    const locked = superOnly && isSuperAdmin === false;
    return (
      <Card style={{ paddingTop: 14, paddingBottom: 14, paddingLeft: 14, paddingRight: 14, cursor: locked ? "default" : "pointer", opacity: locked ? .55 : 1, minHeight: 44 }}
        onClick={locked ? undefined : () => d({ type: "TAB", tab })}>
        <div data-testid={`hub-tile-${tab}`} aria-disabled={locked || undefined}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12, background: `${color}12`, border: `1px solid ${color}20`,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              {typeof icon === "function" ? icon({ size: 18, color }) : <span style={{ fontSize: 18 }}>{icon}</span>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: V.text }}>{label}</div>
              <div style={{ fontSize: 10, color: V.text3, marginTop: 1 }}>{desc}</div>
              {locked && <div style={{ fontSize: 10, color: V.warn, marginTop: 3, fontWeight: 700 }}>🔒 {gateReason}</div>}
            </div>
            {!locked && Icons.chevRight({ size: 14, color: V.text3 })}
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 20 }}>🔐</span>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: V.text }}>Admin Tools</div>
          <div style={{ fontSize: 10, color: V.danger, fontWeight: 600 }}>Server-verified access</div>
        </div>
      </div>
      <Section icon="📊" label="Dashboard" desc="Platform stats, trends & overview" color={V.accent} tab="admin_dashboard" />
      <Section icon="👥" label="User Management" desc="All users, search, sort & filter" color="#06b6d4" tab="admin_users" />
      <Section icon="🔍" label="User Lookup" desc="Full drill-down — workouts, photos, vault, settings" color={V.purple} tab="admin_lookup" />
      <Section icon="💬" label="Content Moderation" desc="View DMs, group chats, activity" color="#ec4899" tab="admin_moderation" />
      <Section icon="🩺" label="Platform Health" desc="Stale users, sync issues, session data" color={V.warn} tab="admin_health" />
      <Section icon="📣" label="Push Broadcast" desc="Send notifications to all users or a specific user" color={V.danger} tab="admin_push" />
      <Section icon="⚡" label="XP & Ranks" desc="View stats, adjust XP, and override ranks for any user" color="#f59e0b" tab="admin_xp" />
      <Section icon="📈" label="Business Value" desc="Analytics, retention, engagement & growth insights" color="#22c55e" tab="admin_biz" />
      <Section icon="🔎" label="User Audit" desc="Full read-only view of any user's data — every tab they see" color="#a78bfa" tab="admin_audit" />
      <Section icon="🏢" label="Organizations" desc="Gyms & coaches — tenants, branding, invites, seats, suspend" color="#0ea5e9" tab="admin_orgs" superOnly />
      <Section icon="📊" label="Org Analytics" desc="White-label growth, seats, adherence & per-tenant metrics" color="#14b8a6" tab="admin_org_analytics" superOnly />
      {isSuperAdmin === false && (
        <div style={{ fontSize: 10, color: V.text3, lineHeight: 1.5, paddingTop: 2, paddingLeft: 2, paddingRight: 2 }}>
          {adminEmailConfigured === false
            ? "Tenant screens need ADMIN_EMAIL set on the API project — until it is, nobody is the super-admin."
            : "Tenant screens are limited to the super-admin (the address in ADMIN_EMAIL)."}
        </div>
      )}
    </div>
  );
}
